import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles } from "lucide-react";
import type { EnterpriseCustomField } from "@/hooks/useEnterpriseCustomFields";

export type CustomFieldValues = Record<string, string | number | boolean>;

interface EnterpriseCustomFieldsStepProps {
  fields: EnterpriseCustomField[];
  values: CustomFieldValues;
  onChange: (values: CustomFieldValues) => void;
}

const EnterpriseCustomFieldsStep = ({ fields, values, onChange }: EnterpriseCustomFieldsStepProps) => {
  const setVal = (key: string, value: any) => onChange({ ...values, [key]: value });

  return (
    <Card className="border-border bg-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-foreground">
          <Sparkles className="h-5 w-5 text-primary" />
          Custom Enterprise Fields
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          These fields were configured for your organization in Enterprise Settings. They will be saved with the proposal and included in the PDF.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {fields.map((f) => {
          const v = values[f.field_key];
          return (
            <div key={f.id} className="space-y-1.5">
              <Label className="text-foreground">
                {f.field_label}
                {f.is_required && <span className="text-destructive ml-1">*</span>}
              </Label>
              {f.help_text && <p className="text-xs text-muted-foreground">{f.help_text}</p>}

              {f.field_type === "text" && (
                <Input
                  value={(v as string) ?? ""}
                  onChange={(e) => setVal(f.field_key, e.target.value)}
                  className="bg-background border-border"
                />
              )}
              {f.field_type === "textarea" && (
                <Textarea
                  value={(v as string) ?? ""}
                  onChange={(e) => setVal(f.field_key, e.target.value)}
                  className="bg-background border-border min-h-[100px]"
                />
              )}
              {f.field_type === "number" && (
                <Input
                  type="number"
                  value={v === undefined || v === null ? "" : String(v)}
                  onChange={(e) => setVal(f.field_key, e.target.value === "" ? "" : Number(e.target.value))}
                  className="bg-background border-border"
                />
              )}
              {f.field_type === "date" && (
                <Input
                  type="date"
                  value={(v as string) ?? ""}
                  onChange={(e) => setVal(f.field_key, e.target.value)}
                  className="bg-background border-border"
                />
              )}
              {f.field_type === "select" && (
                <Select value={(v as string) ?? ""} onValueChange={(val) => setVal(f.field_key, val)}>
                  <SelectTrigger className="bg-background border-border">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {f.options.map((opt) => (
                      <SelectItem key={opt} value={opt}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {f.field_type === "checkbox" && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={!!v}
                    onCheckedChange={(checked) => setVal(f.field_key, !!checked)}
                  />
                  <span className="text-sm text-muted-foreground">Yes</span>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default EnterpriseCustomFieldsStep;
